/**
 * Column layout for the two content tables (SPEC decision 22, the `columns:posts` pattern).
 *
 * `columns:content-posts` / `columns:content-comments` — **global**, read at mount, written back
 * debounced 500 ms. Unknown or missing ids fall back to the canonical order in
 * `packages/shared/dto/content.ts`, which stays the single source of truth for what a column is.
 * The blob sanitizer itself — including the sentinel that re-seeds a layout written before the
 * registry last *reordered* its columns — lives in `contentState.ts`, where the node suite can
 * reach it. The two tables carry their own sentinels and move independently, and the Posts table
 * alone pins a column — `config.pinned`, normalised on every read and write, sentinel-independent.
 *
 * Like `useColumnLayout`, the layout is derived from the TanStack Query cache and edits go through
 * `setQueryData`: the Columns picker lives in the header while the table renders elsewhere, so two
 * hook instances exist and must never diverge. Unlike it, this one is **parameterised** by a config
 * object rather than copied per table — the two registries differ only in their ids, and a
 * duplicated 120-line hook is two chances for them to drift apart.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CONTENT_COMMENTS_COLUMNS_UI_STATE_KEY,
  CONTENT_COMMENTS_COLUMN_IDS,
  CONTENT_POSTS_COLUMNS_UI_STATE_KEY,
  CONTENT_POSTS_COLUMN_IDS,
  DEFAULT_VISIBLE_CONTENT_COMMENTS_COLUMNS,
  DEFAULT_VISIBLE_CONTENT_POSTS_COLUMNS,
  type ContentCommentsColumnId,
  type ContentPostsColumnId,
} from '@lw/shared';
import { getUiState, putUiState } from '../../api/uiState';
import { QK } from '../../api/queryKeys';
import { useDebouncedCallback } from '../../lib/hooks';
import { pinColumnFirst } from '../view/viewState';
import {
  contentColumnLayoutDefaults,
  sanitizeContentColumnLayout,
  type ContentColumnLayoutConfig,
  type ContentStoredLayout,
} from './contentState';

const WRITE_DEBOUNCE_MS = 500;

export type { ContentColumnLayoutConfig };

export const CONTENT_POSTS_LAYOUT: ContentColumnLayoutConfig<ContentPostsColumnId> = {
  storageKey: CONTENT_POSTS_COLUMNS_UI_STATE_KEY,
  ids: CONTENT_POSTS_COLUMN_IDS,
  defaultVisible: DEFAULT_VISIBLE_CONTENT_POSTS_COLUMNS,
  // Bumped when the seven rates were regrouped at the tail: a *move* of ids the stored order
  // already names is invisible to the neighbour insert, so the one-shot re-seed is the only way.
  sentinel: 'sendsRate',
  // The post name stays at the left edge while the other 30 columns scroll under it (§12.3).
  pinned: 'post',
};

export const CONTENT_COMMENTS_LAYOUT: ContentColumnLayoutConfig<ContentCommentsColumnId> = {
  storageKey: CONTENT_COMMENTS_COLUMNS_UI_STATE_KEY,
  ids: CONTENT_COMMENTS_COLUMN_IDS,
  defaultVisible: DEFAULT_VISIBLE_CONTENT_COMMENTS_COLUMNS,
  sentinel: 'impressionsX',
};

type StoredLayout<Id extends string> = ContentStoredLayout<Id>;

export type ContentColumnLayoutHandle<Id extends string> = {
  ready: boolean;
  /** Every known column, in display order. */
  order: Id[];
  hidden: Set<Id>;
  /** The frozen first column, or `null` for a table that pins nothing (Comments). */
  pinned: Id | null;
  setOrder: (next: Id[]) => void;
  toggle: (id: Id) => void;
  reset: () => void;
};

export function useContentColumnLayout<Id extends string>(
  config: ContentColumnLayoutConfig<Id>,
): ContentColumnLayoutHandle<Id> {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QK.uiState(config.storageKey),
    queryFn: ({ signal }) => getUiState<unknown>(config.storageKey, signal),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // A failed read falls back to the canonical layout rather than blocking the table for ever —
  // seeding the cache keeps every hook instance on the same fallback.
  const isError = query.isError;
  useEffect(() => {
    if (!isError) return;
    const key = QK.uiState(config.storageKey);
    if (queryClient.getQueryData(key) !== undefined) return;
    queryClient.setQueryData(key, contentColumnLayoutDefaults(config));
  }, [isError, queryClient, config]);

  const layout = useMemo(
    () => (query.data === undefined ? null : sanitizeContentColumnLayout<Id>(query.data, config)),
    [query.data, config],
  );

  const persist = useDebouncedCallback((next: StoredLayout<Id>) => {
    void putUiState(config.storageKey, next).catch(() => {
      // Layout persistence is best-effort — the table still works from the cached state.
    });
  }, WRITE_DEBOUNCE_MS);

  // Every edit reads the cache, not component state, so a change made in one instance (the header
  // menu) is immediately the base for the next change in another (a header drag in the table).
  const commit = useCallback(
    (updater: (current: StoredLayout<Id>) => StoredLayout<Id>) => {
      const current = sanitizeContentColumnLayout<Id>(
        queryClient.getQueryData(QK.uiState(config.storageKey)),
        config,
      );
      const next = updater(current);
      queryClient.setQueryData(QK.uiState(config.storageKey), next);
      persist(next);
    },
    [persist, queryClient, config],
  );

  const setOrder = useCallback(
    (next: Id[]) => {
      const ordered = pinColumnFirst(
        [...next, ...config.ids.filter((id) => !next.includes(id))],
        config.pinned ?? null,
      );
      commit((current) => ({ ...current, order: ordered }));
    },
    [commit, config.ids, config.pinned],
  );

  const toggle = useCallback(
    (id: Id) => {
      commit((current) => ({
        ...current,
        hidden: current.hidden.includes(id)
          ? current.hidden.filter((entry) => entry !== id)
          : [...current.hidden, id],
      }));
    },
    [commit],
  );

  const reset = useCallback(() => commit(() => contentColumnLayoutDefaults(config)), [commit, config]);

  const effective = layout ?? contentColumnLayoutDefaults(config);

  return {
    ready: layout !== null,
    order: effective.order,
    hidden: new Set(effective.hidden),
    pinned: config.pinned ?? null,
    setOrder,
    toggle,
    reset,
  };
}
